import axios from 'axios';
import FileSaver from 'file-saver';
import config from '../config/Constant';

export const API_BASE_URL = config.urls.api_base_url;

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Utility functions
const prepareResponse = (response) => {
  let err = false;
  let error_message = '';
  if (response.data === undefined) {
    error_message = 'Api Failed to fetch the data';
    err = true;
  }

  if (response.data && response.data.error) {
    error_message = response.data.message;
    err = true;
  }

  if (err) {
    return {
      success: false,
      error: true,
      message: error_message,
      status_code: response.data.status_code,
      code: response.data.code,
      data: response.data.data || {},
    };
  } else {
    const responseData = {
      success: true,
      error: false,
      message: response.data.message,
      status_code: response.data.status_code,
      code: response.data.code,
      data: response.data.data,
    };

    if (response.data.pagination_data) {
      responseData.pagination = response.data.pagination_data;
    }
    return responseData;
  }
};

const prepareCatchResponse = (err) => {
  const responseData = err.response && err.response.data;

  if (responseData) {
    return {
      success: false,
      error: true,
      message: responseData.message || `${err.name} - ${err.message}`,
      status_code: responseData.status_code || err.response.status,
      code: responseData.code || err.response.status,
      data: responseData.data || {},
    };
  }

  return {
    success: false,
    error: true,
    message: `${err.name} - ${err.message}`,
    status_code: err.status || err.code,
    code: err.status || err.code,
    data: {},
  };
};

// Legacy Methods with Deprecation Notices
/**
 * @deprecated Use postRequestWithoutAccessTokenAsync instead
 */
export function postRequestWithoutAccessToken(url, data, next) {
  axios
    .post(API_BASE_URL + url, data)
    .then((response) => {
      next(prepareResponse(response));
    })
    .catch((error) => {
      next(prepareCatchResponse(error));
    });
}

/**
 * @deprecated Use getRequestAsync instead
 */
export function getRequest(url, data, next) {
  axios
    .get(API_BASE_URL + url, data)
    .then((response) => {
      next(prepareResponse(response));
    })
    .catch((err) => {
      next(prepareCatchResponse(err));
    });
}

/**
 * @deprecated Use getStaticRequestAsync instead
 */
export function getStaticRequest(url, data, next) {
  axios
    .get(API_BASE_URL + url, data)
    .then((response) => {
      next(response);
    })
    .catch((err) => {
      next(prepareCatchResponse(err));
    });
}

/**
 * @deprecated Use getRequestWithoutAccessTokenAsync instead
 */
export const getRequestWithoutAccessToken = async (url, next) => {
  try {
    const res = await axios.get(API_BASE_URL + url);
    next(prepareResponse(res));
  } catch (err) {
    next(prepareCatchResponse(err));
  }
};

/**
 * @deprecated Use postRequestAsync instead
 */
export function postRequest(url, data, access_token, next) {
  const headers = {
    headers: {
      Authorization: access_token,
    },
  };
  axios
    .post(API_BASE_URL + url, data, headers)
    .then((response) => {
      next(prepareResponse(response));
    })
    .catch((err) => {
      console.log('err ', err);
      next(prepareCatchResponse(err));
    });
}

/**
 * @deprecated Use putRequestAsync instead
 */
export function putRequest(url, data, access_token, next) {
  const headers = {
    headers: {
      Authorization: access_token,
    },
  };
  axios
    .put(API_BASE_URL + url, data, headers)
    .then((response) => {
      next(prepareResponse(response));
    })
    .catch((err) => {
      next(prepareCatchResponse(err));
    });
}

/**
 * @deprecated Use deleteRequestAsync instead
 */
export function deleteRequest(url, data, next) {
  axios
    .delete(API_BASE_URL + url, data)
    .then((response) => {
      next(prepareResponse(response));
    })
    .catch((err) => {
      next(prepareCatchResponse(err));
    });
}

/**
 * @deprecated Use patchRequestAsync instead
 */
export function patchRequest(url, data, access_token, next) {
  const headers = {
    headers: {
      Authorization: access_token,
    },
  };
  axios
    .patch(API_BASE_URL + url, data, headers)
    .then((response) => {
      next(prepareResponse(response));
    })
    .catch((err) => {
      console.log('err ', err);
      next(prepareCatchResponse(err));
    });
}

/**
 * @deprecated Use downloadFileAsync instead
 */
export function downloadFile(params, next) {
  const { url, data, access_token, fileName } = params;

  const headers = {
    headers: {
      Authorization: access_token.token,
    },
    responseType: 'blob',
  };
  axios
    .post(API_BASE_URL + url, data, headers)
    .then((response) => {
      if (response.status == 200) {
        FileSaver.saveAs(response.data, fileName);
      }
      next(prepareResponse(response));
    })
    .catch((err) => {
      console.error(err);
    });
}

/**
 * @deprecated Use uploadFileAsync instead
 */
export function uploadFile(url, file, access_token, next) {
  const headers = {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: access_token,
    },
  };

  const formData = new FormData();
  formData.append('file', file);

  axios
    .post(API_BASE_URL + url, formData, headers)
    .then((response) => {
      next(prepareResponse(response));
    })
    .catch((err) => {
      console.log('err ', err);
      next(prepareCatchResponse(err));
    });
}

// Modern Async Methods
export const postRequestWithoutAccessTokenAsync = async (url, data) => {
  try {
    const response = await api.post(url, data);
    return prepareResponse(response);
  } catch (error) {
    return prepareCatchResponse(error);
  }
};

export const getRequestAsync = async (url, config = {}) => {
  try {
    const response = await api.get(url, config);
    return prepareResponse(response);
  } catch (error) {
    return prepareCatchResponse(error);
  }
};

export const getStaticRequestAsync = async (url, params) => {
  try {
    const response = await api.get(url, { params });
    return response;
  } catch (error) {
    return prepareCatchResponse(error);
  }
};

export const getRequestWithoutAccessTokenAsync = async (url) => {
  try {
    const response = await api.get(url);
    return prepareResponse(response);
  } catch (error) {
    return prepareCatchResponse(error);
  }
};

export const postRequestAsync = async (url, data, access_token) => {
  try {
    const headers = access_token ? { Authorization: access_token } : {};
    const response = await api.post(url, data, { headers });
    return prepareResponse(response);
  } catch (error) {
    return prepareCatchResponse(error);
  }
};

export const putRequestAsync = async (url, data, access_token) => {
  try {
    const headers = access_token ? { Authorization: access_token } : {};
    const response = await api.put(url, data, { headers });
    return prepareResponse(response);
  } catch (error) {
    return prepareCatchResponse(error);
  }
};

export const deleteRequestAsync = async (url, config = {}) => {
  try {
    const response = await api.delete(url, config);
    return prepareResponse(response);
  } catch (error) {
    return prepareCatchResponse(error);
  }
};

export const patchRequestAsync = async (url, data, access_token) => {
  try {
    const headers = access_token ? { Authorization: access_token } : {};
    const response = await api.patch(url, data, { headers });
    return prepareResponse(response);
  } catch (error) {
    return prepareCatchResponse(error);
  }
};

export const downloadFileAsync = async (params) => {
  const { url, data, access_token, fileName } = params;
  try {
    const response = await api.post(url, data, {
      headers: { Authorization: access_token.token },
      responseType: 'blob',
    });

    if (response.status === 200) {
      FileSaver.saveAs(response.data, fileName);
    }
    return prepareResponse(response);
  } catch (error) {
    console.error('Download failed:', error);
    return prepareCatchResponse(error);
  }
};

export const uploadFileAsync = async (url, file, access_token) => {
  try {
    // Create FormData - keep it simple like the original uploadFile function
    const formData = new FormData();
    formData.append('file', file);

    // Set up headers exactly like the original uploadFile function
    const headers = {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: access_token,
      },
    };

    // Log request details for debugging
    console.log('Upload URL:', API_BASE_URL + url);

    // Use axios directly with the full URL to match the original uploadFile function
    const response = await axios.post(API_BASE_URL + url, formData, headers);

    return prepareResponse(response);
  } catch (error) {
    console.error('Upload failed:', error);

    // Keep the improved error logging
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      console.error('Error response headers:', error.response.headers);
    } else if (error.request) {
      console.error('Error request:', error.request);
    } else {
      console.error('Error message:', error.message);
    }

    return prepareCatchResponse(error);
  }
};

// Request Management Utilities
export const createCancelToken = () => {
  return axios.CancelToken.source();
};

export const cancelAllRequests = (message = 'Operation canceled') => {
  api.interceptors.request.use((config) => {
    config.cancelToken = new axios.CancelToken((cancel) => cancel(message));
    return config;
  });
};
